import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { createProject, deleteProject, fetchProjects } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";

const inputClass =
  "rounded-lg border border-adriatic/25 bg-white px-3 py-2 text-sm text-adriatic-deep placeholder:text-adriatic-mid/50 focus:border-acqua focus:ring-2 focus:ring-acqua/30 focus:outline-none disabled:opacity-60";

/**
 * The project catalog: what the grants below can be drawn from, and what
 * every downstream project's own slug must match. Removing a project here
 * removes every grant referencing it and re-syncs whoever held one
 * (apps/api/src/admin/routes.ts), so custom:apps never mentions a
 * project that no longer exists.
 */
export function ProjectsPanel() {
  const queryClient = useQueryClient();
  const projects = useQuery({ queryKey: ["admin", "projects"], queryFn: fetchProjects });

  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () => createProject({ slug, name }),
    onSuccess: () => {
      setSlug("");
      setName("");
      queryClient.invalidateQueries({ queryKey: ["admin", "projects"] });
    },
  });

  const remove = useMutation({
    mutationFn: (targetSlug: string) => deleteProject(targetSlug),
    // A removal can change what other people's custom:apps says, so every
    // admin query is worth refetching rather than just this panel's own.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin"] }),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!create.isPending && slug && name) create.mutate();
  }

  return (
    <section className="rounded-2xl bg-white p-6 shadow-sm">
      <h2 className="text-base font-semibold text-adriatic-deep">Projects</h2>
      <p className="mt-1 text-sm text-adriatic-mid">
        The catalog every grant below is drawn from. The slug must match the project's own subdomain
        label.
      </p>

      <form onSubmit={onSubmit} className="mt-4 flex flex-wrap items-end gap-3" noValidate>
        <div className="space-y-1">
          <label htmlFor="project-slug" className="block text-xs font-medium text-adriatic-deep">
            Slug
          </label>
          <input
            id="project-slug"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            placeholder="catalog"
            pattern="^[a-z0-9]([a-z0-9-]*[a-z0-9])?$"
            required
            disabled={create.isPending}
            className={`${inputClass} w-40`}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="project-name" className="block text-xs font-medium text-adriatic-deep">
            Name
          </label>
          <input
            id="project-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Catalog"
            required
            disabled={create.isPending}
            className={`${inputClass} w-56`}
          />
        </div>
        <button
          type="submit"
          disabled={create.isPending || !slug || !name}
          className="rounded-full bg-coral px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-coral-light hover:text-adriatic-deep focus-visible:ring-2 focus-visible:ring-acqua/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {create.isPending ? "Adding..." : "Add project"}
        </button>
      </form>
      {errorMessage(create.error) && (
        <p role="alert" className="mt-2 text-sm text-coral-ink">
          {errorMessage(create.error)}
        </p>
      )}

      <div className="mt-5 overflow-x-auto">
        {projects.isPending ? (
          <p className="text-sm text-adriatic-mid">Loading...</p>
        ) : projects.isError ? (
          <p role="alert" className="text-sm text-coral-ink">
            {errorMessage(projects.error)}
          </p>
        ) : projects.data.length === 0 ? (
          <p className="text-sm text-adriatic-mid">No projects in the catalog yet.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-adriatic/15 text-xs text-adriatic-mid">
                <th className="pb-2 font-medium">Slug</th>
                <th className="pb-2 font-medium">Name</th>
                <th className="pb-2 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {projects.data.map((project) => (
                <tr key={project.slug} className="border-b border-adriatic/10 last:border-0">
                  <td className="py-2 font-mono text-adriatic-deep">{project.slug}</td>
                  <td className="py-2 text-adriatic-deep">{project.name}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => remove.mutate(project.slug)}
                      disabled={remove.isPending}
                      className="text-xs font-medium text-coral-ink hover:underline disabled:opacity-60"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {errorMessage(remove.error) && (
        <p role="alert" className="mt-2 text-sm text-coral-ink">
          {errorMessage(remove.error)}
        </p>
      )}
    </section>
  );
}
